// src/services/coinService.js
//
// SBL COINS — the browser side.
//
// The browser can only READ a wallet (firestore.rules: wallets write:false).
// Every coin comes from a Cloud Function in functions/coins.js, which
// applies the limits. This file:
//
//   * asks for coins at the right moments (end of a game, first time a
//     topic's video / song / sing-along is finished, a teacher's gift)
//   * listens to the wallet so every coin counter on screen stays live
//   * announces each award with a "sbl:coins-earned" window event, which
//     the CoinCounter turns into the coins-flying-in animation
//
// Nothing here can break a game: every call is wrapped, and a failure just
// means no coins that time (it is logged, never shown as an error to a child).

import { useEffect, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../firebase/firebaseConfig";

const announce = (detail) => {
  try {
    window.dispatchEvent(new CustomEvent("sbl:coins-earned", { detail }));
  } catch {
    /* no window */
  }
};

const callSafely = async (name, data) => {
  try {
    const res = await httpsCallable(functions, name)(data);
    return res?.data || null;
  } catch (err) {
    console.warn(`[coins] ${name} failed:`, err?.code || err?.message || err);
    return null;
  }
};

/**
 * End of a game (UniversalQuizGame's Final Score screen). Resolves
 * { amount, coins, why } — amount 0 with why "daily-cap" / "replay" etc.
 */
export const awardGameCoins = async ({ slug, title, quizScore, bonusScore, correct, from }) => {
  if (!getAuth().currentUser || !slug) return null;
  const out = await callSafely("awardGameCoins", { slug, title, quizScore, bonusScore, correct });
  if (out?.amount > 0) announce({ amount: out.amount, coins: out.coins, reason: "game", from });
  return out;
};

/**
 * End of a Treasure Quest: moves the quest on and pays for the treasure
 * chest (server decides). Resolves { questLevel, amount, coins } or null.
 */
export const finishTreasureQuest = async ({ boardsWon, treasure, from }) => {
  if (!getAuth().currentUser) return null;
  const out = await callSafely("finishTreasureQuest", { boardsWon, treasure });
  if (out?.amount > 0) announce({ amount: out.amount, coins: out.coins, reason: "treasure", from });
  return out;
};

/** First time a topic's video / song / sing-along is finished. */
export const awardFirstPlayCoins = async ({ kind, slug, title }) => {
  if (!getAuth().currentUser || !slug || !kind) return null;
  const out = await callSafely("awardFirstPlayCoins", { kind, slug, title });
  if (out?.amount > 0) announce({ amount: out.amount, coins: out.coins, reason: `first-${kind}` });
  return out;
};

/**
 * An educator gives coins to students on their roster. Unlike the others
 * this one reports errors, because a teacher pressed a button and should
 * hear why it didn't work. Resolves { given, capped: [uid], notInClass: [uid] }.
 */
export const giveClassCoins = async ({ studentUids, amount, reason, fromBank = false }) => {
  try {
    const res = await httpsCallable(functions, "giveClassCoins")({ studentUids, amount, reason, fromBank });
    return res.data;
  } catch (err) {
    const code = String(err?.code || "");
    if (code.includes("not-found")) throw new Error("Coins aren't switched on yet (the giveClassCoins function isn't deployed).");
    throw new Error(err?.message || String(err));
  }
};

/* ------------------------------------------------------------------ */
/* Live wallet                                                         */
/* ------------------------------------------------------------------ */

/**
 * The signed-in user's wallet, live: { coins, lifetimeCoins, questLevel, loaded }.
 * Anyone without a wallet yet simply has 0 coins.
 */
export const useWallet = () => {
  const [wallet, setWallet] = useState({ coins: 0, lifetimeCoins: 0, questLevel: 0, loaded: false });
  useEffect(() => {
    let stopDoc = () => {};
    const stopAuth = onAuthStateChanged(getAuth(), (user) => {
      stopDoc();
      if (!user) {
        setWallet({ coins: 0, lifetimeCoins: 0, loaded: true });
        return;
      }
      stopDoc = onSnapshot(
        doc(db, "wallets", user.uid),
        (snap) => {
          const d = snap.exists() ? snap.data() : {};
          setWallet({
            coins: Number(d.coins) || 0,
            lifetimeCoins: Number(d.lifetimeCoins) || 0,
            questLevel: Number(d.questLevel) || 0,
            trail: d.trail || null, // step 5: { step, world, lastDay }
            classBank: Number(d.classBank) || 0, // teachers: bought + daily-visit coins for the class
            hasClassBank: d.classBank !== undefined,
            // a "running low" nudge only makes sense once the bank has been big
            bankWasBig: Number(d.classBankBought) > 0 || Number(d.classBankEarned) >= 100,
            loaded: true,
          });
        },
        // rules not deployed yet, offline… — show 0, never break the page
        () => setWallet((w) => ({ ...w, loaded: true }))
      );
    });
    // a chest, an album prize, a game… tells us the new total straight away:
    // show it now instead of waiting for the database to catch up
    const onEarned = (e) => {
      const total = Number(e.detail?.coins);
      if (!e.detail?.bank && Number.isFinite(total) && total > 0) setWallet((w) => (total > w.coins ? { ...w, coins: total } : w));
    };
    window.addEventListener("sbl:coins-earned", onEarned);
    return () => {
      stopDoc();
      stopAuth();
      window.removeEventListener("sbl:coins-earned", onEarned);
    };
  }, []);
  return wallet;
};
